// src/components/EditCollectionModal.js

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';

const EditCollectionModal = ({
  open,
  onClose,
  guildId,
  currentCollectionAddress,
  onUpdateSuccess,
  onUpdateFailure,
}) => {
  const [newCollectionAddress, setNewCollectionAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Reset state when the modal is opened or closed
  useEffect(() => {
    if (open) {
      setNewCollectionAddress('');
      setLoading(false);
      setSnackbar({ open: false, message: '', severity: 'success' });
    }
  }, [open]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async () => {
    console.log('handleSubmit called');
    console.log('guildId:', guildId);
    console.log('currentCollectionAddress:', currentCollectionAddress);
    console.log('newCollectionAddress before trim:', newCollectionAddress);

    // Defensive check
    if (!currentCollectionAddress) {
      console.error('Current Collection Address is undefined.');
      setSnackbar({ open: true, message: 'Current collection address is missing.', severity: 'error' });
      return;
    }

    if (typeof newCollectionAddress !== 'string') {
      setSnackbar({ open: true, message: 'New collection address is invalid.', severity: 'error' });
      return;
    }

    const trimmedNewCollectionAddress = newCollectionAddress.trim();

    // Manual Validation
    if (!trimmedNewCollectionAddress) {
      setSnackbar({ open: true, message: 'New collection address cannot be empty.', severity: 'warning' });
      return;
    }

    // Optional: Add more validation rules (e.g., format of collectionAddress)
    const isValidSolanaAddress = (address) => {
      // Basic validation: Solana addresses are base58 encoded strings with 32-44 characters
      const solanaAddressRegex = /^[A-HJ-NP-Za-km-z1-9]{32,44}$/;
      return solanaAddressRegex.test(address);
    };

    if (!isValidSolanaAddress(trimmedNewCollectionAddress)) {
      setSnackbar({ open: true, message: 'Invalid collection address format.', severity: 'warning' });
      return;
    }

    setLoading(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://mindguardnft.com';

      const response = await fetch(`${API_BASE_URL}/api/server-config/${guildId}/collections/update-address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Include authorization headers if required
          // 'Authorization': `Bearer YOUR_ACCESS_TOKEN`,
        },
        body: JSON.stringify({
          currentCollectionAddress: currentCollectionAddress.trim(),
          newCollectionAddress: trimmedNewCollectionAddress,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update collection address.');
      }

      setSnackbar({ open: true, message: data.message || 'Collection updated successfully!', severity: 'success' });
      onUpdateSuccess(data);
      setNewCollectionAddress('');
      onClose();
    } catch (error) {
      console.error('Error updating collection:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
      onUpdateFailure(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Edit Collection Address</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Collection Address"
            type="text"
            fullWidth
            variant="outlined"
            value={newCollectionAddress}
            onChange={(e) => setNewCollectionAddress(e.target.value)}
            disabled={loading}
            helperText="Enter the new NFT collection address."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

EditCollectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  guildId: PropTypes.string.isRequired,
  currentCollectionAddress: PropTypes.string.isRequired,
  onUpdateSuccess: PropTypes.func,
  onUpdateFailure: PropTypes.func,
};

EditCollectionModal.defaultProps = {
  onUpdateSuccess: () => {},
  onUpdateFailure: () => {},
};

export default EditCollectionModal;

